'use client';
import type { PointsTableEntry } from '@/lib/tourData';
import Image from 'next/image';

interface PointsTableProps {
  data: PointsTableEntry[];
}

function FormBadge({ result }: { result: 'W' | 'L' | 'N' }) {
  return (
    <span
      className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
        result === 'W' ? 'bg-rc-green/20 text-rc-green' :
        result === 'L'? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/40'
      }`}
    >
      {result}
    </span>
  );
}

export default function PointsTable({ data }: PointsTableProps) {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[700px] border-collapse">
        <thead>
          <tr style={{ backgroundColor: 'rgb(18, 23, 33)' }}>
            <th className="text-left px-4 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider w-8">#</th>
            <th className="text-left px-4 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Team</th>
            <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">P</th>
            <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">W</th>
            <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">L</th>
            <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">T</th>
            <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">NR</th>
            <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">NRR</th>
            <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">PTS</th>
            <th className="text-center px-3 py-3 text-white/50 text-xs font-semibold uppercase tracking-wider">Form</th>
          </tr>
        </thead>
        <tbody>
          {data?.map((entry, index) => (
            <tr
              key={entry.team.id}
              className="border-t border-white/[0.06] hover:bg-white/[0.03] transition-colors"
            >
              <td className="px-4 py-3.5">
                <span className={`text-sm font-bold ${
                  index < 2 ? 'text-rc-orange' : 'text-white/40'
                }`}>
                  {index + 1}
                </span>
              </td>
              <td className="px-4 py-3.5">
                <div className="flex items-center gap-3">
                  {entry.team.logo ? (
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-rc-border flex-shrink-0">
                      <Image
                        src={entry.team.logo}
                        alt={`${entry.team.name} cricket logo`}
                        width={32}
                        height={32}
                        className="w-full h-full object-cover"
                        unoptimized
                      />
                    </div>
                  ) : (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold"
                      style={{ backgroundColor: entry.team.color + '33', border: `1px solid ${entry.team.color}66` }}
                    >
                      {entry.team.shortName.slice(0, 3)}
                    </div>
                  )}
                  <div>
                    <p className="text-white/85 text-sm font-medium">{entry.team.name}</p>
                    <p className="text-white/40 text-xs">{entry.team.shortName}</p>
                  </div>
                </div>
              </td>
              <td className="px-3 py-3.5 text-center text-white/70 text-sm">{entry.played}</td>
              <td className="px-3 py-3.5 text-center text-rc-green text-sm font-medium">{entry.won}</td>
              <td className="px-3 py-3.5 text-center text-red-400 text-sm font-medium">{entry.lost}</td>
              <td className="px-3 py-3.5 text-center text-white/50 text-sm">{entry.tied}</td>
              <td className="px-3 py-3.5 text-center text-white/50 text-sm">{entry.noResult}</td>
              <td className={`px-3 py-3.5 text-center text-sm font-medium ${
                entry.nrr.startsWith('+') ? 'text-rc-green' : 'text-red-400'
              }`}>
                {entry.nrr}
              </td>
              <td className="px-3 py-3.5 text-center">
                <span className="text-white font-bold text-sm">{entry.points}</span>
              </td>
              <td className="px-3 py-3.5">
                <div className="flex items-center justify-center gap-1">
                  {entry.form?.map((f, i) => (
                    <FormBadge key={i} result={f} />
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-3 border-t border-white/[0.06]">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-rc-orange" />
          <span className="text-white/40 text-xs">Qualified for Playoffs</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-rc-green text-xs font-bold">W</span>
          <span className="text-white/40 text-xs">Win</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-red-400 text-xs font-bold">L</span>
          <span className="text-white/40 text-xs">Loss</span>
        </div>
      </div>
    </div>
  );
}
